// The guest invitation page's link to our server for the "Download the Mazoom app" box, partner
// offers and the guest's four actions. Kept tiny: it is part of the page's first download.
//
// - Its switches come from our public settings list (GET /api/v1/app-config, section guest_page),
//   read once per page after the invitation has loaded. If that call fails, or the section is missing
//   or malformed, everything here stays off: nothing is shown and nothing is sent.
// - Actions go to our server only (POST /api/v1/guest-events), in the background with sendBeacon
//   (fetch keepalive as a fallback); a failure is ignored and never shown.
// - The guest is a random ID kept in this browser. Never the guest's phone, name, or the invitation
//   link (its hash identifies the guest).

export type GuestPlacement = 'invitation' | 'after_confirm' | 'partner_offer'
export type GuestStore = 'app_store' | 'google_play'
export type GuestDevice = 'ios' | 'android' | 'desktop'
export type GuestLanguage = 'ar' | 'en'

export interface GuestPageConfig {
  events: boolean
  downloadPrompt: boolean
  partnerOffers: boolean
  storeLinks: Record<GuestPlacement, Record<GuestStore, string>>
}

export interface GuestEvent {
  name: 'invitation_viewed' | 'app_download_prompt_shown' | 'app_download_clicked' | 'partner_offer_viewed'
  event_id: number
  placement?: GuestPlacement
  store?: GuestStore
  offer_id?: number
  language?: GuestLanguage
}

const API: string | undefined = import.meta.env.VITE_REACT_APP_API_BASE_URL
const PLACEMENTS: GuestPlacement[] = ['invitation', 'after_confirm', 'partner_offer']
const STORES: GuestStore[] = ['app_store', 'google_play']
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const BROWSER_KEY = 'mazoom_guest_browser_id'
// A view repeated within this time in the same tab is the same view (the app reloads itself on a
// first visit, a few seconds after the page opens).
const VIEW_WINDOW_MS = 60000

let configRequest: Promise<GuestPageConfig | null> | null = null

/** The guest page's switches; null (everything off) when they can't be read. Fetched once per page. */
export function loadGuestPageConfig(): Promise<GuestPageConfig | null> {
  if (!configRequest) configRequest = fetchConfig()
  return configRequest
}

async function fetchConfig(): Promise<GuestPageConfig | null> {
  try {
    if (!API || typeof fetch !== 'function') return null
    const controller = typeof AbortController === 'function' ? new AbortController() : null
    const timer = controller ? setTimeout(() => controller.abort(), 10000) : null
    const res = await fetch(`${API}/v1/app-config`, {
      credentials: 'omit',
      referrerPolicy: 'no-referrer',
      headers: { Accept: 'application/json' },
      signal: controller ? controller.signal : undefined,
    })
    if (timer) clearTimeout(timer)
    if (!res.ok) return null
    const body = await res.json()
    return parseGuestPageConfig(body && body.data ? body.data.guest_page : null)
  } catch {
    return null
  }
}

/** The section as the server sent it, checked; null when it isn't usable. */
export function parseGuestPageConfig(raw: any): GuestPageConfig | null {
  if (!raw || typeof raw !== 'object') return null
  const links = {} as GuestPageConfig['storeLinks']
  for (const placement of PLACEMENTS) {
    links[placement] = {} as Record<GuestStore, string>
    for (const store of STORES) {
      const url = raw.store_links && raw.store_links[placement] ? raw.store_links[placement][store] : null
      links[placement][store] = typeof url === 'string' && /^https:\/\/[^\s"'<>`]+$/i.test(url) ? url : ''
    }
  }
  return {
    events: raw.events === true,
    downloadPrompt: raw.download_prompt === true,
    partnerOffers: raw.partner_offers === true,
    storeLinks: links,
  }
}

/** iPhone/iPad, Android, or anything else (desktop). */
export function guestDevice(): GuestDevice {
  try {
    const ua = navigator.userAgent || ''
    if (/iPhone|iPad|iPod/i.test(ua) || (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1)) return 'ios'
    if (/Android/i.test(ua)) return 'android'
  } catch {
    // fall through
  }
  return 'desktop'
}

let browser: string | null = null

function browserId(): string {
  if (browser) return browser
  let id: string | null = null
  try {
    id = window.localStorage.getItem(BROWSER_KEY)
  } catch {
    id = null
  }
  if (!id || !UUID.test(id)) {
    id = newUuid()
    try {
      window.localStorage.setItem(BROWSER_KEY, id)
    } catch {
      // Private mode: this page's ID only.
    }
  }
  browser = id.toLowerCase()
  return browser
}

function newUuid(): string {
  const c: any = typeof crypto !== 'undefined' ? crypto : null
  if (c && typeof c.randomUUID === 'function') return c.randomUUID()
  const b = new Uint8Array(16)
  if (c && typeof c.getRandomValues === 'function') c.getRandomValues(b)
  else for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256)
  b[6] = (b[6] & 0x0f) | 0x40
  b[8] = (b[8] & 0x3f) | 0x80
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('')
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`
}

let queue: Array<GuestEvent & { device: GuestDevice }> = []
let flushTimer: ReturnType<typeof setTimeout> | null = null
let listening = false

/**
 * Records one action. Views wait up to 1.5 s so a page's first few go in one request; a click goes
 * at once (the guest may be leaving for the store). Never throws.
 */
export function trackGuest(event: GuestEvent, now = false): void {
  try {
    if (!API) return
    queue.push({ ...event, device: guestDevice() })
    if (!listening) {
      listening = true
      // Whatever is waiting goes before the page is hidden or closed.
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') flushGuestEvents()
      })
      window.addEventListener('pagehide', flushGuestEvents)
    }
    if (now || queue.length >= 10) flushGuestEvents()
    else if (!flushTimer) flushTimer = setTimeout(flushGuestEvents, 1500)
  } catch {
    // never shown
  }
}

/** Records a view at most once a minute in this tab (see VIEW_WINDOW_MS). */
export function trackGuestView(event: GuestEvent): void {
  try {
    const key = `mazoom_guest_seen:${event.name}:${event.event_id}:${event.placement || ''}:${event.offer_id || ''}`
    let last = 0
    try {
      last = Number(window.sessionStorage.getItem(key)) || 0
    } catch {
      last = 0
    }
    const at = Date.now()
    if (last && at - last < VIEW_WINDOW_MS) return
    try {
      window.sessionStorage.setItem(key, String(at))
    } catch {
      // not kept: counted again on a reload
    }
    trackGuest(event)
  } catch {
    // never shown
  }
}

export function flushGuestEvents(): void {
  try {
    if (flushTimer) {
      clearTimeout(flushTimer)
      flushTimer = null
    }
    while (queue.length && API) {
      const body = JSON.stringify({ browser_id: browserId(), events: queue.splice(0, 10) })
      const url = `${API}/v1/guest-events`
      let sent = false
      try {
        // A string body is sent as text/plain, so no CORS preflight.
        sent = typeof navigator.sendBeacon === 'function' && navigator.sendBeacon(url, body)
      } catch {
        sent = false
      }
      if (!sent && typeof fetch === 'function') {
        fetch(url, {
          method: 'POST',
          body,
          keepalive: true,
          mode: 'no-cors',
          credentials: 'omit',
          referrerPolicy: 'no-referrer',
          headers: { 'Content-Type': 'text/plain' },
        }).catch(() => undefined)
      }
    }
  } catch {
    // never shown
  }
}
